/**
 * A doctor's address, in the format of whichever country it is in. The request
 * of 6 October 2026 (fifth), section 2.
 *
 * A doctor may be anywhere, so an address is no longer one box of free text:
 * it is a country, and then the parts that country's post asks for, in the
 * order its labels are written. Which parts those are, whether the country has
 * a state or a region, what its postal code is called and looks like, and how
 * the lines of a label run, all come from one list of 250 countries
 * (`countries.json`, built from Google's address metadata, the data behind
 * libaddressinput, CC BY 4.0) that the API and the app share word for word.
 *
 * The rules here are the server's, sentence for sentence and in its order: the
 * mock server answers with them, the form checks with them before it sends,
 * and the label a screen draws is the label the server would send.
 */

/** What a country calls its first level division, as the form labels the field. */
export const REGION_LABELS = [
  'province',
  'state',
  'island',
  'department',
  'parish',
  'county',
  'oblast',
  'area',
  'prefecture',
  'district',
  'do_si',
] as const;
export type RegionLabel = (typeof REGION_LABELS)[number];

/**
 * What a country calls its postal code. `pobox` is a country that has none:
 * the field is a PO box there, never required and held to no pattern.
 */
export const POSTAL_KINDS = ['postal', 'zip', 'pin', 'eircode', 'pobox'] as const;
export type PostalKind = (typeof POSTAL_KINDS)[number];

/** One country of `countries.json`: its name and how its addresses are written. */
export interface CountryEntry {
  /** ISO 3166-1 alpha-2, in capitals. */
  code: string;
  name: string;
  /**
   * The label, top to bottom. `%N` name, `%O` clinic, `%A` the street lines,
   * `%D` area, `%C` city, `%S` region, `%Z` postal code, `%P` PO box; anything
   * else is written as it stands.
   */
  lines: string[];
  /** Which of C, S and Z the post wants in capitals. */
  upper: string;
  /** Present only where the country has a region field. */
  region?: { label: RegionLabel; required: boolean };
  postal: { kind: PostalKind; required: boolean; pattern?: string; example?: string };
  /** `[short code, name]` pairs the label prints in place of the name: CA for California. */
  regionCodes?: [string, string][];
}

/**
 * How two names are compared wherever a place is looked up: without accents,
 * without regard to case, and without the spaces round them. Unicode NFD,
 * every combining mark dropped, lower case, trimmed, in that order, as the
 * server folds one.
 */
export function fold(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Capitals as the server writes them: the German sharp s as SS, so a German
 * name reads as a browser would write it, and then one character for one
 * character. Any other letter whose capital is more than one letter is left
 * as it is, which is what an invariant upper case does.
 */
export function upperInvariant(text: string): string {
  return Array.from(text.replace(/\u00df/g, 'SS'), (letter) => {
    const capital = letter.toUpperCase();
    return Array.from(capital).length === 1 ? capital : letter;
  }).join('');
}

/** The country a code names, in either case, or undefined when it is none of the list. */
export function countryOf(
  countries: readonly CountryEntry[],
  code: string | null | undefined,
): CountryEntry | undefined {
  const wanted = (code ?? '').trim().toUpperCase();
  return wanted === '' ? undefined : countries.find((country) => country.code === wanted);
}

/** Whether a country's addresses have a state or a region at all. The Emirates have none: the city covers it. */
export function hasRegion(country: CountryEntry | undefined): boolean {
  return country?.region !== undefined;
}

/** Whether the region must be filled in. */
export function regionRequired(country: CountryEntry | undefined): boolean {
  return country?.region?.required === true;
}

/** What the country calls its region, for the field's label, or null where it has none. */
export function regionLabelOf(country: CountryEntry | undefined): RegionLabel | null {
  return country?.region?.label ?? null;
}

/** What the country calls its postal code: `postal` where nothing says otherwise. */
export function postalKindOf(country: CountryEntry | undefined): PostalKind {
  return country?.postal.kind ?? 'postal';
}

/** Whether the postal code must be filled in. A PO box never must. */
export function postalRequired(country: CountryEntry | undefined): boolean {
  return country?.postal.required === true;
}

/** One postal code that is valid in the country, for the hint under the field, or null. */
export function postalExampleOf(country: CountryEntry | undefined): string | null {
  return country?.postal.example ?? null;
}

/**
 * Whether a postal code is one the country's post would accept: the whole of
 * it matches the country's pattern, without regard to case. A country with no
 * pattern accepts anything.
 */
export function postalMatches(country: CountryEntry | undefined, postalCode: string): boolean {
  const pattern = country?.postal.pattern;
  return pattern === undefined || new RegExp(`^(?:${pattern})$`, 'i').test(postalCode);
}

/* ---- the address itself ---- */

/** The eleven keys of an address on the wire, in the order the server writes them. */
export const ADDRESS_KEYS = [
  'country',
  'countryName',
  'region',
  'regionCode',
  'city',
  'cityId',
  'area',
  'line1',
  'line2',
  'postalCode',
  'lines',
] as const;
export type AddressKey = (typeof ADDRESS_KEYS)[number];

/**
 * A doctor's address as the API serves it. Complete once somebody has saved
 * it; while the doctor's `addressNeedsReview` is true it is only what the
 * conversion of the old text was sure of, and then `city` and `line1` may be
 * null.
 */
export interface DoctorAddress {
  /** ISO 3166-1 alpha-2, in capitals. */
  country: string;
  countryName: string | null;
  region: string | null;
  /** The region's code inside the country (`03`, `CA`) when it is one of the list, else null. */
  regionCode: string | null;
  city: string | null;
  /** The city's id in the list, as text, when it is one of the list, else null. */
  cityId: string | null;
  area: string | null;
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
  /** The label, top to bottom, the country's name last. The name and the clinic are the reader's own lines above it. */
  lines: string[];
}

/** The nine parts of an address that are kept: everything but the country's name and the label. */
export interface AddressRecord {
  country: string;
  region: string | null;
  regionCode: string | null;
  city: string | null;
  cityId: string | null;
  area: string | null;
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
}

/**
 * An address as a form sends it: the country, and the parts as they were
 * typed or chosen. An address is all or nothing, so it travels whole: the
 * server replaces the address with this one. `regionCode` and `cityId` say the
 * region and the city were chosen from the list; send the words beside them
 * all the same.
 */
export interface AddressInput {
  country?: string | null;
  region?: string | null;
  regionCode?: string | null;
  city?: string | null;
  cityId?: string | null;
  area?: string | null;
  line1?: string | null;
  line2?: string | null;
  postalCode?: string | null;
}

/** The longest each part may be, as the columns are, measured once tidied. */
export const ADDRESS_REGION_MAX = 120;
export const ADDRESS_CITY_MAX = 120;
export const ADDRESS_AREA_MAX = 120;
export const ADDRESS_LINE_MAX = 200;
export const ADDRESS_POSTAL_CODE_MAX = 20;

/** A part as it is kept: trimmed, every run of white space inside it one space, and null rather than empty. */
export function tidyAddressText(value: string | null | undefined): string | null {
  const text = (value ?? '').trim().replace(/\s+/g, ' ');
  return text === '' ? null : text;
}

/** `PO Box` as people write it in front of the number: P.O. Box, POB, P O Box:, PO Box #. */
const PO_BOX_PREFIX = /^p\.?\s*o\.?\s*b(ox)?\.?\s*[:#]?\s*/i;

/**
 * A postal code as it is kept: tidied as every part is, then in capitals; and
 * where the country has no postal code and the field is a PO box, without the
 * words `PO Box` somebody typed in front of the number.
 */
export function tidyPostalCode(
  value: string | null | undefined,
  country: CountryEntry | undefined,
): string | null {
  const text = tidyAddressText(value);
  if (text === null) return null;

  const capitals = upperInvariant(text);
  if (postalKindOf(country) !== 'pobox') return capitals;
  return tidyAddressText(capitals.replace(PO_BOX_PREFIX, ''));
}

/**
 * The number of a PO box written out in full, `PO Box 123` in any spelling, or
 * null for anything else. What the conversion of the old text looks for in a
 * country whose addresses carry a PO box.
 */
export function poBoxNumber(text: string): string | null {
  const rest = text.trim().replace(PO_BOX_PREFIX, '');
  return rest !== text.trim() && /^\d+$/.test(rest) ? rest : null;
}

/** An address with every part tidied. The country is its code in capitals, or empty when none was sent. */
export interface TidyAddress extends Omit<AddressRecord, 'regionCode' | 'cityId'> {
  /** As sent, trimmed: null when the region was typed and not chosen. */
  regionCode: string | null;
  cityId: string | null;
}

/** What was sent, as the server reads it before it checks anything. */
export function tidyAddress(input: AddressInput, country: CountryEntry | undefined): TidyAddress {
  return {
    country: (input.country ?? '').trim().toUpperCase(),
    region: tidyAddressText(input.region),
    regionCode: tidyAddressText(input.regionCode),
    city: tidyAddressText(input.city),
    cityId: tidyAddressText(input.cityId),
    area: tidyAddressText(input.area),
    line1: tidyAddressText(input.line1),
    line2: tidyAddressText(input.line2),
    postalCode: tidyPostalCode(input.postalCode, country),
  };
}

/** The refusals of an address, worded once for the mock server, the form and the tests. The API sends the same. */
export const ADDRESS_REFUSAL = {
  /** `address` sent as anything but null or an object of parts. */
  notParts: 'Send the address as its parts',
  /** The old key, from a copy of the app older than the parts. */
  outOfDate: 'This copy of the app is out of date. Refresh it to enter an address',
  chooseCountry: 'Choose a country from the list',
  chooseRegion: 'Choose a region from the list',
  regionRequired: 'Enter the state or region',
  chooseCity: 'Choose a city from the list',
  cityRequired: 'Enter the city',
  line1Required: 'Enter the building and street',
  /** On `clinic`: an address is delivered to a clinic or a company, so one with an address has a name. */
  clinicRequired: 'Enter the clinic or company name',
} as const;

/** What a sentence calls the postal code, by what the country calls it. */
const POSTAL_WORDS: Record<PostalKind, string> = {
  postal: 'postal code',
  zip: 'ZIP code',
  pin: 'PIN code',
  eircode: 'Eircode',
  // A PO box is never required and has no pattern, so neither sentence is ever said of one.
  pobox: 'postal code',
};

/** `Enter the postal code`, in the country's own word for it. */
export function postalRequiredMessage(country: CountryEntry | undefined): string {
  return `Enter the ${POSTAL_WORDS[postalKindOf(country)]}`;
}

/** `Enter a valid postal code, like 95014`, in the country's own word and with its own example. */
export function postalInvalidMessage(country: CountryEntry | undefined): string {
  const example = postalExampleOf(country);
  const sentence = `Enter a valid ${POSTAL_WORDS[postalKindOf(country)]}`;
  return example === null ? sentence : `${sentence}, like ${example}`;
}

/** The keys an address is refused on, as the server names them in `errors`. */
export const ADDRESS_PROBLEM_FIELDS = [
  'address.country',
  'address.region',
  'address.city',
  'address.area',
  'address.line1',
  'address.line2',
  'address.postalCode',
] as const;
export type AddressProblemField = (typeof ADDRESS_PROBLEM_FIELDS)[number];

/** Something wrong with one part of an address: the key it is on, and the server's sentence. */
export interface AddressProblem {
  field: AddressProblemField;
  message: string;
}

function tooLong(max: number): string {
  return `Keep it to ${String(max)} characters`;
}

/**
 * Everything wrong with an address as sent, ONE message for each key that has
 * something wrong, in the server's order. Empty when the address will do.
 *
 * A country that is missing or not one of the list is the only thing said of
 * such an address: nothing else can be checked without knowing whose rules
 * apply. After that each part is asked in turn: chosen from the list where it
 * says it was, present where the country requires it, and within its length.
 * A country with no region field is asked nothing about a region, whatever
 * was sent for one.
 *
 * `geo` is the lists of regions and cities, for whoever holds them: the
 * server, and the mock server with its small ones. With them a `regionCode`
 * and a `cityId` are held to the lists, and the region and the city are
 * SETTLED before they are asked for (`resolveAddress`): a city chosen by its
 * id needs no words beside it, and gives the address its own region, so a
 * required region left blank is no fault when the city supplies one. A form
 * has no lists and hands none in: it is then asked for the words of both, so
 * it sends the words beside the code and the id, which is right either way.
 */
export function addressProblems(
  input: AddressInput,
  countries: readonly CountryEntry[],
  geo?: GeoLookup,
): AddressProblem[] {
  const country = countryOf(countries, input.country);
  if (country === undefined) {
    return [{ field: 'address.country', message: ADDRESS_REFUSAL.chooseCountry }];
  }

  const sent = tidyAddress(input, country);
  const settled = geo === undefined ? sent : resolveAddress(sent, country, geo);
  const problems: AddressProblem[] = [];
  const refuse = (field: AddressProblemField, message: string | null) => {
    if (message !== null) problems.push({ field, message });
  };
  const over = (value: string | null, max: number) =>
    value !== null && value.length > max ? tooLong(max) : null;

  // A code that is nobody's is refused even when the city beside it would have supplied the region.
  const strayRegion =
    geo !== undefined &&
    sent.regionCode !== null &&
    geo.region(country.code, sent.regionCode) === undefined;
  // An id is digits and nothing else, whoever checks it; whether it is a city is the lists' to say.
  const strayCity =
    sent.cityId !== null &&
    (!/^\d+$/.test(sent.cityId) ||
      (geo !== undefined && geo.city(country.code, sent.cityId) === undefined));

  if (hasRegion(country)) {
    refuse(
      'address.region',
      strayRegion
        ? ADDRESS_REFUSAL.chooseRegion
        : regionRequired(country) && settled.region === null
          ? ADDRESS_REFUSAL.regionRequired
          : over(settled.region, ADDRESS_REGION_MAX),
    );
  }
  refuse(
    'address.city',
    strayCity
      ? ADDRESS_REFUSAL.chooseCity
      : settled.city === null
        ? ADDRESS_REFUSAL.cityRequired
        : over(settled.city, ADDRESS_CITY_MAX),
  );
  refuse('address.area', over(sent.area, ADDRESS_AREA_MAX));
  refuse(
    'address.line1',
    sent.line1 === null ? ADDRESS_REFUSAL.line1Required : over(sent.line1, ADDRESS_LINE_MAX),
  );
  refuse('address.line2', over(sent.line2, ADDRESS_LINE_MAX));
  refuse(
    'address.postalCode',
    sent.postalCode === null
      ? postalRequired(country)
        ? postalRequiredMessage(country)
        : null
      : !postalMatches(country, sent.postalCode)
        ? postalInvalidMessage(country)
        : over(sent.postalCode, ADDRESS_POSTAL_CODE_MAX),
  );
  return problems;
}

/* ---- what is kept, once the checks pass ---- */

/** A region of the list: its code inside the country, and its name. */
export interface GeoRegion {
  code: string;
  name: string;
}

/** A city of the list: its id as text, its name, and the region it is in, where the list says. */
export interface GeoCity {
  id: string;
  name: string;
  regionCode: string | null;
}

/**
 * The lists of regions and cities, as the rules that link a typed name to one
 * of them read them. The server's are its two tables; the mock server's are
 * its small seed. Every name is compared folded.
 */
export interface GeoLookup {
  /** The region of a country by its code, in either case, or undefined. */
  region(country: string, code: string): GeoRegion | undefined;
  /** Every region of the country whose name, or plain name, folds to this. */
  regionsNamed(country: string, folded: string): GeoRegion[];
  /** The city of a country by its id, or undefined. */
  city(country: string, id: string): GeoCity | undefined;
  /** Every city of the country whose name, or plain name, folds to this. */
  citiesNamed(country: string, folded: string): GeoCity[];
}

/**
 * An address as it is kept, from one that passed its checks: what was typed is
 * linked to the lists wherever that is certain, and never guessed.
 *
 *   region  none at all in a country with no region field, whatever was sent.
 *           A code sent: the list's own name for it. No code, and words that
 *           fold to exactly one region of the country: that region. Otherwise
 *           the words as typed, with no code.
 *   city    An id sent: the list's own name for it. No id, and words that fold
 *           to exactly one city of the country (and of the region, once one is
 *           known): that city. Otherwise the words as typed, with no id.
 *
 * A city of the list that carries a region gives the address its region,
 * whatever was sent, wherever the country has the field.
 */
export function resolveAddress(
  sent: TidyAddress,
  country: CountryEntry,
  geo: GeoLookup,
): AddressRecord {
  let region: string | null = null;
  let regionCode: string | null = null;

  if (hasRegion(country)) {
    const chosen = sent.regionCode === null ? undefined : geo.region(country.code, sent.regionCode);
    const named = sent.region === null ? [] : geo.regionsNamed(country.code, fold(sent.region));
    const linked = chosen ?? (named.length === 1 ? named[0] : undefined);

    region = linked?.name ?? sent.region;
    regionCode = linked?.code ?? null;
  }

  const byId = sent.cityId === null ? undefined : geo.city(country.code, sent.cityId);
  const alike =
    sent.city === null
      ? []
      : geo
          .citiesNamed(country.code, fold(sent.city))
          .filter((each) => regionCode === null || each.regionCode === regionCode);
  const city = byId ?? (alike.length === 1 ? alike[0] : undefined);

  // The city's own region, wherever the country has the field and the list says which it is.
  const cityRegion =
    city === undefined || city.regionCode === null || !hasRegion(country)
      ? undefined
      : geo.region(country.code, city.regionCode);

  return {
    country: country.code,
    region: cityRegion?.name ?? region,
    regionCode: cityRegion?.code ?? regionCode,
    city: city?.name ?? sent.city,
    cityId: city?.id ?? null,
    area: sent.area,
    line1: sent.line1,
    line2: sent.line2,
    postalCode: sent.postalCode,
  };
}

/* ---- the label ---- */

/** What the label is written from: the parts, any of them missing. */
export type AddressLabelParts = Partial<Omit<AddressRecord, 'country'>>;

/** The letters a line of a country's format stands a part in with. */
type LabelToken = 'N' | 'O' | 'A' | 'D' | 'C' | 'S' | 'Z' | 'P';

/**
 * The lines of an address as its country's post writes them, top to bottom,
 * the country's name last. The name and the clinic are not among them: they
 * are the caller's own lines, above these.
 *
 * Each line of the country's format is filled in from the parts: the area, the
 * city, the region (its short code where the country prints one, CA for
 * California), and the postal code, or `PO Box` and the number in a country
 * that has no postal code. What the post wants in capitals is put in capitals.
 * Then every run of white space becomes one space, the punctuation a missing
 * part left at either end is dropped, and a line with nothing left is not
 * written. The street lines are written as they were typed, where the format
 * gives them a line to themselves, and nowhere else.
 *
 * The server's formatter, step for step: `address-fixtures.json` holds twenty
 * addresses both must write alike.
 */
export function addressLines(address: AddressLabelParts, country: CountryEntry): string[] {
  const pobox = country.postal.kind === 'pobox';
  const postalCode = address.postalCode ?? '';
  const typedRegion = country.region === undefined ? '' : (address.region ?? '');
  const shortCode = (country.regionCodes ?? []).find(
    ([, name]) => fold(name) === fold(typedRegion),
  )?.[0];

  const street = [address.line1 ?? '', address.line2 ?? ''].filter((line) => line !== '');

  const values: Record<LabelToken, string> = {
    N: '',
    O: '',
    // The street lines are written only where `%A` stands alone, as lines of their own.
    A: '',
    D: address.area ?? '',
    C: address.city ?? '',
    S: shortCode ?? typedRegion,
    Z: pobox ? '' : postalCode,
    P: pobox && postalCode !== '' ? `PO Box ${postalCode}` : '',
  };
  for (const letter of country.upper) {
    if (letter === 'C' || letter === 'S' || letter === 'Z') {
      values[letter] = upperInvariant(values[letter]);
    }
  }

  const lines: string[] = [];
  for (const template of country.lines) {
    if (template === '%A') {
      lines.push(...street);
      continue;
    }

    const line = template
      .replace(/%([NOADCSZP])/g, (_token, letter: LabelToken) => values[letter])
      .replace(/\s+/g, ' ')
      .replace(/^[ ,\-/]+|[ ,\-/]+$/g, '');
    if (line !== '') lines.push(line);
  }

  lines.push(country.name);
  return lines;
}

/**
 * An address as the wire carries it, from the nine parts that are kept: with
 * the country's name and the label beside them. A country that is not one of
 * the list has no name and no format, and its label is whatever parts it has.
 */
export function addressOnWire(
  record: AddressRecord,
  countries: readonly CountryEntry[],
): DoctorAddress {
  const country = countryOf(countries, record.country);
  return {
    country: record.country,
    countryName: country?.name ?? null,
    region: record.region,
    regionCode: record.regionCode,
    city: record.city,
    cityId: record.cityId,
    area: record.area,
    line1: record.line1,
    line2: record.line2,
    postalCode: record.postalCode,
    lines:
      country === undefined
        ? [record.line1, record.line2, record.area, record.city, record.region, record.postalCode]
            .filter((part): part is string => part !== null)
        : addressLines(record, country),
  };
}

/** Whether an address has everything a delivery needs: a city and a street line. One under review may have neither. */
export function addressComplete(address: Pick<DoctorAddress, 'city' | 'line1'> | null): boolean {
  return address !== null && address.city !== null && address.line1 !== null;
}

/**
 * What a form starts from: the parts of the address a doctor has, as the nine
 * a form sends back, or an empty address in the country given for a doctor
 * with none.
 */
export function addressInputOf(
  address: DoctorAddress | null,
  defaultCountry: string,
): Required<AddressInput> {
  return {
    country: address?.country ?? defaultCountry,
    region: address?.region ?? null,
    regionCode: address?.regionCode ?? null,
    city: address?.city ?? null,
    cityId: address?.cityId ?? null,
    area: address?.area ?? null,
    line1: address?.line1 ?? null,
    line2: address?.line2 ?? null,
    postalCode: address?.postalCode ?? null,
  };
}

/** The country a new address starts in: the lab's own. The request of 6 October 2026 (fifth), section 2. */
export const DEFAULT_COUNTRY = 'AE';

/**
 * Whether two addresses, as a form holds them, are the same address once each
 * is tidied: what tells a form that somebody changed a part, so the address is
 * sent, from one that only gained a space.
 */
export function sameAddress(
  a: AddressInput,
  b: AddressInput,
  countries: readonly CountryEntry[],
): boolean {
  const left = tidyAddress(a, countryOf(countries, a.country));
  const right = tidyAddress(b, countryOf(countries, b.country));
  return (Object.keys(left) as (keyof TidyAddress)[]).every((key) => left[key] === right[key]);
}
